'use client';

import { createContext, useContext } from 'react';

/** One answered field, as the Review block lists it. */
export interface AnswerSummary {
    key: string;
    label: string;
    value: unknown;
}

/**
 * Navigation surface exposed by the wizard to blocks inside a step (notably the
 * draggable "Next" button block). Keeps the built-in chrome minimal — advancing
 * is driven by a block the form author places, not a fixed nav bar.
 */
export interface WizardNav {
    goNext: () => void;
    goBack: () => void;
    isFirst: boolean;
    /** True at the end of the branch these answers take, where Next submits. */
    isLast: boolean;
    isSubmitting: boolean;
    /** False when the current step is a sign-in gate and the visitor isn't authenticated. */
    canProceed: boolean;
    /**
     * Answers on the path actually taken, in page order — what a Review block
     * shows. Excludes conditionally hidden fields and any branch abandoned
     * along the way, so it matches what gets submitted.
     */
    answers: AnswerSummary[];
}

const noop = () => {};

export const WizardNavContext = createContext<WizardNav>({
    goNext: noop,
    goBack: noop,
    isFirst: true,
    isLast: false,
    isSubmitting: false,
    canProceed: true,
    answers: []
});

export const useWizardNav = () => useContext(WizardNavContext);
